/**
 * Unified Column Configuration
 *
 * Single source of truth for all column type configurations:
 * - Field types (text, number, date, dropdown, etc.) - for table columns and placeholders
 * - Custom column types (linkable, chip, status, actions, etc.) - for specialized rendering
 * - Editable column renderers - for inline editing
 * - Renderer registry and single entry point (renderColumnCell)
 *
 * NOTE: Field type implementations are in the fieldTypes/ folder.
 * This file assembles and exports the unified configuration.
 */

import type { ColumnDef } from '@tanstack/react-table';
import type { ComboBoxApi } from 'components/inputs/types';
import type { FieldTypeConfig, TypeConfig, ResolvedField } from './types';
import type { CustomColumn, ColumnRenderer, RenderOptions } from './columnModel';

// Import directly from source files to avoid circular dependencies
// Field type configs (common per-type files)
import { dropdownFieldType } from './columnTypes/dropdown';
import { statusFieldType, statusTypeConfig } from './columnTypes/status';
import { booleanFieldType, booleanTypeConfig } from './columnTypes/boolean';
import { textFieldType } from './columnTypes/text';
import { descriptionFieldType } from './columnTypes/description';
import { multiselectFieldType } from './columnTypes/multiselect';
import { numberFieldType } from './columnTypes/number';
import { dateFieldType } from './columnTypes/date';
import { dateRangeFieldType } from './columnTypes/dateRange';
import { tagsFieldType } from './columnTypes/tags';

// Inline renderers / aliases + inline-only field types (and their configs)
import {
  actionsReadOnly,
  actionsEditable,
  dropdownReadOnly,
  dropdownEditable,
  statusReadOnly,
  statusEditable,
  booleanReadOnly,
  booleanEditable,
  textReadOnly,
  textEditable,
  descriptionReadOnly,
  descriptionEditable,
  multiselectReadOnly,
  multiselectEditable,
  numberReadOnly,
  numberEditable,
  dateReadOnly,
  dateEditable,
  switchFieldType,
  switchReadOnly,
  switchEditable,
  tagsReadOnly,
  tagsEditable,
  tagsDropdownFieldType,
  tagsDropdownReadOnly,
  tagsDropdownEditable,
  linkableReadOnly,
  linkableEditable,
  chipReadOnly,
  chipEditable,
  maskedReadOnly,
  maskedEditable,
  customReadOnly,
  customEditable,
} from './columnTypes/inline';

// Import lookup registry from fieldTypes
import {
  getRegistry as fieldTypesGetRegistry,
  getLookupApi as fieldTypesGetLookupApi,
  resetLookupRegistryForTesting as fieldTypesResetLookupRegistryForTesting,
} from './columnTypes/registry';

// Import centralized initialization
import {
  initializeColumnConfigDependencies,
  setResolveFieldConfig as setInitResolveFieldConfig,
  setGetTypeConfig as setInitGetTypeConfig,
} from './initDependencies';

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Master configuration for all field types
 * Defines placeholder, table renderer, and sizes for each type
 *
 * Types: text, number, date, dateRange, dropdown, switch, status, boolean
 */
export const fieldTypeConfig: Record<string, FieldTypeConfig> = {
  text: textFieldType,
  description: descriptionFieldType,
  multiselect: multiselectFieldType,
  number: numberFieldType,
  date: dateFieldType,
  dateRange: dateRangeFieldType,
  dd: dropdownFieldType,
  'tags-dd': tagsDropdownFieldType,
  switch: switchFieldType,
  status: statusFieldType,
  boolean: booleanFieldType,
  tags: tagsFieldType,
};

// ============================================================================
// Unified Renderer Registry
// ============================================================================

/**
 * Registry of read-only renderers for each field type
 * Maps type name → read-only renderer function
 *
 * Note: Type assertions are needed because ColumnRenderer is generic,
 * but the registry stores renderers that work with any T constraint.
 */
export const readOnlyRenderers: Record<string, ColumnRenderer | null> = {
  text: textReadOnly as ColumnRenderer,
  description: descriptionReadOnly as ColumnRenderer,
  multiselect: multiselectReadOnly as ColumnRenderer,
  number: numberReadOnly as ColumnRenderer,
  date: dateReadOnly as ColumnRenderer,
  dateRange: dateReadOnly as ColumnRenderer,
  dd: dropdownReadOnly as ColumnRenderer,
  'tags-dd': tagsDropdownReadOnly as ColumnRenderer,
  status: statusReadOnly as ColumnRenderer,
  boolean: booleanReadOnly as ColumnRenderer,
  switch: switchReadOnly as ColumnRenderer,
  tags: tagsReadOnly as ColumnRenderer,
  linkable: linkableReadOnly as ColumnRenderer,
  chip: chipReadOnly as ColumnRenderer,
  masked: maskedReadOnly as ColumnRenderer,
  actions: actionsReadOnly as ColumnRenderer,
  custom: customReadOnly as ColumnRenderer,
};

/**
 * Registry of editable renderers for each field type
 * Maps type name → editable renderer function (or null if not editable)
 *
 * Note: Type assertions are needed because ColumnRenderer is generic,
 * but the registry stores renderers that work with any T constraint.
 */
export const editableRenderers: Record<string, ColumnRenderer | null> = {
  text: textEditable as ColumnRenderer,
  description: descriptionEditable as ColumnRenderer,
  multiselect: multiselectEditable as ColumnRenderer,
  number: numberEditable as ColumnRenderer,
  date: dateEditable as ColumnRenderer,
  dateRange: dateEditable as ColumnRenderer,
  dd: dropdownEditable as ColumnRenderer,
  'tags-dd': tagsDropdownEditable as ColumnRenderer,
  status: statusEditable as ColumnRenderer,
  boolean: booleanEditable as ColumnRenderer,
  switch: switchEditable as ColumnRenderer,
  tags: tagsEditable as ColumnRenderer,
  linkable: linkableEditable,
  chip: chipEditable,
  masked: maskedEditable,
  actions: actionsEditable,
  custom: customEditable as ColumnRenderer,
};

// ============================================================================
// Helper Functions - Field Types
// ============================================================================

/**
 * Get the placeholder for a field based on its type
 * Falls back to text type if type is not found
 */
export const getFieldPlaceholder = (type: string | undefined, label: string): string => {
  const config = fieldTypeConfig[type || 'text'] || fieldTypeConfig.text;
  if (!config.placeholder) return '';
  return typeof config.placeholder === 'function' ? config.placeholder(label) : config.placeholder;
};

// ============================================================================
// Single Entry Point
// ============================================================================

/**
 * Determine if a column should use editable rendering
 */
const isColumnEditable = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): boolean => {
  const editable = col.editable;
  const hasOnUpdate = typeof col.onUpdate === 'function';

  // For function-based editability, we need editable state to evaluate per-row (from context in cells)
  // For boolean editability, we just need the flag to be true
  const isEditable = editable === true || typeof editable === 'function';

  return isEditable && hasOnUpdate;
};

/**
 * Get the effective type for a column
 * Handles both `type` and `customType` properties
 */
const getColumnType = <T extends { id: number | string }>(col: CustomColumn<T>): string => {
  // If customType is set, use it as the type
  if (col.customType) {
    return col.customType;
  }
  // Otherwise use the base type, defaulting to 'text'
  return col.type || 'text';
};

/**
 * Per-feature renderer overrides.
 * When a renderer is provided for a type it takes priority over the shared registry.
 */
export type RendererOverrides = {
  readOnly?: Partial<Record<string, ColumnRenderer>>;
  editable?: Partial<Record<string, ColumnRenderer>>;
};

/**
 * Single entry point for all column rendering
 *
 * Routes to the appropriate renderer based on:
 * 1. Column type (text, dropdown, status, etc.)
 * 2. Whether the column is editable (col.editable + col.onUpdate + editableState)
 * 3. Optional rendererOverrides — feature-level overrides checked before the shared registry
 *
 * Usage:
 * ```tsx
 * const rendered = renderColumnCell(col, {
 *   editableState,
 *   setEditableState,
 *   dateFormatter,
 * });
 * ```
 */
export const renderColumnCell = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  options: RenderOptions<T> = {},
  rendererOverrides?: RendererOverrides
): Partial<ColumnDef<T>> => {
  // Initialize all dependencies on first use (deferred to avoid circular deps)
  initializeColumnConfigDependencies();

  const type = getColumnType(col);
  const shouldUseEditable = isColumnEditable(col, options);

  // Type assertion helper - renderers are stored with base type but work with any T
  const castRenderer = (renderer: ColumnRenderer | null | undefined) =>
    renderer as ((c: CustomColumn<T>, opts: RenderOptions<T>) => Partial<ColumnDef<T>>) | null;

  // Pick the appropriate renderer — feature overrides take priority over the shared registry
  if (shouldUseEditable) {
    const editableRenderer = castRenderer(
      rendererOverrides?.editable?.[type] ?? editableRenderers[type]
    );
    if (editableRenderer) {
      return editableRenderer(col, options);
    }
    // Fall through to read-only if no editable renderer exists
  }

  // Use read-only renderer (feature override first, then shared registry)
  const readOnlyRenderer = castRenderer(
    rendererOverrides?.readOnly?.[type] ?? readOnlyRenderers[type]
  );
  if (readOnlyRenderer) {
    return readOnlyRenderer(col, options);
  }

  // Final fallback to text read-only renderer
  const fallback = castRenderer(readOnlyRenderers.text);
  if (fallback) {
    return fallback(col, options);
  }

  // Should never reach here, but return empty object as safety
  return {};
};

// ============================================================================
// Type Registry - Semantic types for boolean fields
// ============================================================================

/**
 * Type Registry - Maps semantic types to their configurations
 *
 * - `status`: Active/Inactive display (use column id like 'isActive')
 * - `boolean`: True/False display (use column id like 'readyForExport')
 */
export const typeRegistry: Record<string, TypeConfig> = {
  status: statusTypeConfig,
  boolean: booleanTypeConfig,
};

/**
 * Get type config for a semantic type
 * Returns undefined if type is not registered
 */
export const getTypeConfig = (type: string): TypeConfig | undefined => {
  return typeRegistry[type];
};

// ============================================================================
// Lookup Registry - API configurations for dropdown fields
// ============================================================================

/**
 * Lookup Registry - Maps column IDs to their API configurations
 *
 * When a column has type='dd' and no explicit api property,
 * the system will use this registry to find the API config.
 *
 * To add a new lookup:
 * 1. Add the API endpoint to the appropriate api/*.ts file
 * 2. Add an entry here with the column ID as the key
 */
export const getLookupRegistry = fieldTypesGetRegistry;

// ============================================================================
// Field Resolution
// ============================================================================

/**
 * Resolve field configuration based on column type and id
 *
 * The column `id` is always used as the data field name.
 *
 * @param col - Column configuration with id and type
 * @returns Resolved field configuration or null if no special handling needed
 */
export const resolveFieldConfig = (col: { id: string; type?: string }): ResolvedField => {
  const { id, type } = col;

  // Check type registry for status/boolean types
  if (type === 'status' || type === 'boolean') {
    const config = getTypeConfig(type);
    if (config) {
      return {
        kind: type as 'status' | 'boolean',
        options: config.options,
        chipColors: config.chipColors,
      };
    }
  }

  // Check lookup registry for dd (dropdown) type
  if (type === 'dd') {
    const apiConfig = fieldTypesGetLookupApi(id);
    if (apiConfig) {
      return {
        kind: 'api',
        api: apiConfig,
      };
    }
  }

  return null;
};

/**
 * Check if a type is a semantic boolean type (status, boolean, or switch)
 */
export const isSemanticBooleanType = (type?: string): type is 'status' | 'boolean' | 'switch' => {
  return type === 'status' || type === 'boolean' || type === 'switch';
};

/**
 * Reset the cached lookup registry (for testing purposes only)
 * @internal
 */
export const resetLookupRegistryForTesting = fieldTypesResetLookupRegistryForTesting;

// ============================================================================
// Column Helper Constants
// ============================================================================


// ============================================================================
// Module Initialization
// ============================================================================

// Register functions with initDependencies for lazy initialization
// This allows other modules to access these functions without circular imports
setInitResolveFieldConfig(resolveFieldConfig);
setInitGetTypeConfig(getTypeConfig);
